/**
 * FilterBar Component
 * UI for filtering mistakes by exam type, section, difficulty, and sort
 */

import { ChevronDown, RotateCcw, ArrowUpDown } from 'lucide-react';
import { cn } from '@examflow/ui';
import type { MistakeFilters, SortField } from '../services/mistakeFilterModel';
import { getSectionLabel, getDifficultyLabel } from '../services/mistakeFilterModel';

interface FilterBarProps {
  filters: MistakeFilters;
  examTypeOptions: Array<{ value: string; label: string; count: number }>;
  sectionOptions: Array<{ value: string; label: string; count: number }>;
  difficultyOptions: Array<{ value: string; label: string; count: number }>;
  onExamTypeChange: (value: string | null) => void;
  onSectionChange: (value: string | null) => void;
  onDifficultyChange: (value: string | null) => void;
  onSortByChange: (field: SortField) => void;
  onSortDirectionToggle: () => void;
  onReset: () => void;
  hasActiveFilters: boolean;
}

const SORT_OPTIONS: Array<{ value: SortField; label: string }> = [
  { value: 'lastWrongTime', label: '最近错误' },
  { value: 'wrongCount', label: '错误次数' },
  { value: 'difficulty', label: '难度等级' },
  { value: 'nextReviewTime', label: '待复习时间' },
];

export function FilterBar({
  filters,
  examTypeOptions,
  sectionOptions,
  difficultyOptions,
  onExamTypeChange,
  onSectionChange,
  onDifficultyChange,
  onSortByChange,
  onSortDirectionToggle,
  onReset,
  hasActiveFilters,
}: FilterBarProps) {
  return (
    <div className="bg-white rounded-lg border border-[#dbe4d8] p-4 mb-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* Exam Type Filter */}
        <div className="relative">
          <label className="block text-xs font-medium text-slate-500 mb-1">考试类型</label>
          <div className="relative">
            <select
              value={filters.examType ?? ''}
              onChange={(e) => onExamTypeChange(e.target.value || null)}
              className="appearance-none bg-white border border-[#dbe4d8] rounded-[6px] px-3 py-1.5 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 cursor-pointer min-w-[120px]"
            >
              <option value="">全部</option>
              {examTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.count})
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Section Filter */}
        <div className="relative">
          <label className="block text-xs font-medium text-slate-500 mb-1">题目类型</label>
          <div className="relative">
            <select
              value={filters.section ?? ''}
              onChange={(e) => onSectionChange(e.target.value || null)}
              className="appearance-none bg-white border border-[#dbe4d8] rounded-[6px] px-3 py-1.5 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 cursor-pointer min-w-[120px]"
            >
              <option value="">全部</option>
              {sectionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {getSectionLabel(opt.value)} ({opt.count})
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Difficulty Filter */}
        <div className="relative">
          <label className="block text-xs font-medium text-slate-500 mb-1">难度等级</label>
          <div className="relative">
            <select
              value={filters.difficulty ?? ''}
              onChange={(e) => onDifficultyChange(e.target.value || null)}
              className="appearance-none bg-white border border-[#dbe4d8] rounded-[6px] px-3 py-1.5 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 cursor-pointer min-w-[100px]"
            >
              <option value="">全部</option>
              {difficultyOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {getDifficultyLabel(opt.value)} ({opt.count})
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Sort Options */}
        <div className="relative">
          <label className="block text-xs font-medium text-slate-500 mb-1">排序方式</label>
          <div className="flex items-center gap-1">
            <div className="relative">
              <select
                value={filters.sortBy}
                onChange={(e) => onSortByChange(e.target.value as SortField)}
                className="appearance-none bg-white border border-[#dbe4d8] rounded-[6px] px-3 py-1.5 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 cursor-pointer min-w-[110px]"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            </div>
            <button
              onClick={onSortDirectionToggle}
              className={cn(
                'p-1.5 rounded-[6px] border transition-colors',
                filters.sortDirection === 'desc'
                  ? 'bg-primary-50 border-primary-200 text-primary-600'
                  : 'bg-white border-[#dbe4d8] text-slate-500 hover:bg-slate-50'
              )}
              title={filters.sortDirection === 'desc' ? '降序（最新在前）' : '升序（最早在前）'}
            >
              <ArrowUpDown className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Reset Button */}
        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[6px] text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors mt-5"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            重置筛选
          </button>
        )}
      </div>
    </div>
  );
}
